import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Zap, MessageSquare, Brain, ChevronLeft, ChevronRight,
  BarChart3, Mic, TrendingUp, ShieldCheck, Clock
} from 'lucide-react';

// ── AI Feature cards shown in the carousel ──────────────────────────────────
const features = [
  {
    id: 0,
    icon: Mic,
    accentColor: '#A855F7',
    title: 'Consulta por Voz',
    subtitle: 'Pergunte no microfone',
    desc: '"Qual foi a margem de lucro de gasolina comum no último turno?" — receba a resposta em áudio em segundos.',
    stat: '98.5%', statLabel: 'Precisão de Resposta',
  },
  {
    id: 1,
    icon: BarChart3,
    accentColor: '#3B82F6',
    title: 'Relatórios Automáticos',
    subtitle: 'BI sem esforço',
    desc: 'A LukIA gera relatórios de faturamento, margem por produto e performance de turno — direto pelo WhatsApp.',
    stat: '3x', statLabel: 'Mais rápido que ERP manual',
  },
  {
    id: 2,
    icon: TrendingUp,
    accentColor: '#10B981',
    title: 'Previsão de Estoque',
    subtitle: 'Reposição preditiva',
    desc: 'A IA aprende o ritmo do seu posto e avisa antes de faltar combustível — evitando bico parado e venda perdida.',
    stat: '-42%', statLabel: 'Menos ruptura de estoque',
  },
  {
    id: 3,
    icon: ShieldCheck,
    accentColor: '#F59E0B',
    title: 'Auditoria Inteligente',
    subtitle: 'Detecção de anomalias',
    desc: 'A LukIA monitora divergências entre PDV e bombas em tempo real — alertando sobre desvios antes do fechamento.',
    stat: '99.1%', statLabel: 'Taxa de detecção de desvio',
  },
  {
    id: 4,
    icon: Clock,
    accentColor: '#EC4899',
    title: 'Análise de Turno',
    subtitle: 'Performance por frentista',
    desc: 'Compare faturamento, tempo médio por atendimento e percentual de conversão de troca de óleo por turno e frentista.',
    stat: '24/7', statLabel: 'Monitoramento contínuo',
  },
];

const partners = [
  { name: "Integração ANP", icon: "⛽" },
  { name: "Validação Sefaz", icon: "🏢" },
  { name: "Cloud AWS", icon: "☁️" },
  { name: "Certificação PCI", icon: "🔒" }
];

const LukIAVoiceHero = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const feature = features[activeFeature];

  const goNext = () => setActiveFeature(i => (i + 1) % features.length);
  const goPrev = () => setActiveFeature(i => (i - 1 + features.length) % features.length);

  return (
    <div className="w-full min-h-screen bg-linear-to-br from-purple-200 via-fuchsia-200 to-indigo-300 p-4 md:p-10 font-sans selection:bg-purple-100 selection:text-purple-900">

      {/* Main Frame */}
      <div className="relative w-full max-w-7xl mx-auto min-h-[90vh] bg-[#F8F9FA] rounded-[3rem] shadow-[0_40px_100px_-20px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col">

        {/* Header */}
        <header className="w-full px-10 py-7 flex items-center justify-between border-b border-slate-100/80">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 bg-linear-to-tr from-purple-500 to-pink-400 rounded-xl flex items-center justify-center shadow-lg shadow-purple-200">
              <div className="flex flex-col gap-0.5">
                <div className="flex gap-0.5">
                  <div className="w-1.5 h-1.5 bg-white rounded-full opacity-60" />
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                </div>
                <div className="flex gap-0.5">
                  <div className="w-1.5 h-1.5 bg-white rounded-full" />
                  <div className="w-1.5 h-1.5 bg-white rounded-full opacity-60" />
                </div>
              </div>
            </div>
            <span className="text-2xl font-black tracking-tighter bg-clip-text text-transparent bg-linear-to-r from-purple-600 to-slate-900">LukIA</span>
          </div>

          <nav className="hidden lg:flex items-center gap-1 bg-white/80 backdrop-blur-md px-2 py-2 rounded-2xl shadow-sm border border-slate-100">
            {['Recursos', 'Integrações', 'Previsibilidade', 'Sobre'].map((item) => (
              <a key={item} href="#" className="px-5 py-2 text-[13px] font-bold text-slate-500 hover:text-purple-700 hover:bg-slate-50 rounded-xl transition-all">
                {item}
              </a>
            ))}
          </nav>

          <button className="flex items-center gap-2 px-5 py-3 border border-slate-200 rounded-2xl text-purple-700 font-bold text-sm hover:bg-purple-50 transition-all hover:border-purple-200">
            <Zap size={16} strokeWidth={2} />
            Falar com Especialista
          </button>
        </header>

        {/* Hero Content */}
        <div className="flex-1 flex flex-col items-center pt-14 px-6 relative">

          {/* Headline */}
          <div className="text-center max-w-4xl mx-auto z-10">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl md:text-7xl font-bold text-[#1E293B] leading-[1.1] tracking-tight"
            >
              Assuma o{' '}
              <span className="inline-flex items-center gap-3 px-5 py-2 mx-2 bg-white border border-slate-100 rounded-full shadow-sm align-middle">
                <div className="flex items-end gap-1 h-5">
                  {[0.4, 0.7, 0.5, 0.9, 0.6].map((h, i) => (
                    <motion.div
                      key={i}
                      animate={{ scaleY: [h, 1, h * 0.6, 0.9, h] }}
                      transition={{ repeat: Infinity, duration: 1.8, delay: i * 0.15, ease: 'easeInOut' }}
                      className="w-1 bg-purple-400 rounded-full origin-bottom"
                      style={{ height: `${h * 100}%` }}
                    />
                  ))}
                </div>
                <span className="text-2xl md:text-3xl font-black text-purple-600">Controle</span>
                <div className="flex items-end gap-1 h-5">
                  {[0.6, 0.9, 0.5, 0.7, 0.4].map((h, i) => (
                    <motion.div
                      key={i}
                      animate={{ scaleY: [h, 0.6, 1, h * 0.8, h] }}
                      transition={{ repeat: Infinity, duration: 1.8, delay: i * 0.15 + 0.4, ease: 'easeInOut' }}
                      className="w-1 bg-purple-400 rounded-full origin-bottom"
                      style={{ height: `${h * 100}%` }}
                    />
                  ))}
                </div>
              </span>
              {' '}do Pátio com
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-5xl md:text-[80px] font-black text-[#1E293B] leading-none tracking-tighter mt-2"
            >
              Inteligência Artificial <span className="text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-fuchsia-500">LUKIA.</span>
            </motion.div>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="mt-10 mx-auto bg-[#8B5CF6] text-white px-8 py-4 rounded-2xl font-bold shadow-[0_20px_40px_-10px_rgba(139,92,246,0.4)] flex items-center gap-3 hover:bg-[#7C3AED] transition-all"
            >
              Começar com a LukIA Agora
              <ArrowRight size={20} />
            </motion.button>
          </div>

          {/* ── Visualization + Feature Carousel ─────────────────────────────── */}
          <div className="w-full max-w-5xl mx-auto mt-16 relative" style={{ height: 380 }}>

            {/* SVG connector lines */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible" viewBox="0 0 1000 380">
              <path d="M 170,40 L 170,60 L 330,60 L 330,200 L 420,200" fill="none" stroke="#E2E8F0" strokeWidth="1.5" />
              <path d="M 900,100 L 700,100 L 700,190 L 580,190" fill="none" stroke="#E2E8F0" strokeWidth="1.5" />
            </svg>

            {/* Left stat */}
            <div className="absolute left-8 top-0 text-left">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`stat-${activeFeature}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="flex items-center gap-2 text-3xl font-black text-slate-800">
                    <Zap className="w-6 h-6 fill-slate-800 text-slate-800" />
                    <span className="tracking-tighter">{feature.stat}</span>
                  </div>
                  <p className="text-[11px] font-bold text-slate-400 mt-1 uppercase tracking-widest leading-snug">
                    {feature.statLabel}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Left feature chip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="absolute left-[20px] top-[230px] bg-white p-4 px-5 rounded-2xl shadow-xl shadow-purple-900/5 border border-slate-50 flex items-center gap-4 z-20"
            >
              <div className="w-10 h-10 bg-white rounded-xl border border-slate-100 flex items-center justify-center shadow-inner">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-[12px] font-black uppercase text-slate-700 tracking-widest">
                Comandos de Voz <br /> Nativos App
              </p>
            </motion.div>

            {/* Right chip — OpenAI */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="absolute right-[40px] top-[28px] bg-white p-4 rounded-3xl shadow-xl shadow-purple-900/5 border border-slate-50 flex items-center gap-4 z-20"
            >
              <div className="w-10 h-10 bg-[#10A37F] rounded-xl flex items-center justify-center text-white">
                <Brain size={20} />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-0.5">Arquitetura</p>
                <p className="text-sm font-black text-slate-800">OpenAI Engine <span className="text-[#10A37F] ml-1">✓</span></p>
              </div>
            </motion.div>

            {/* Right feature description */}
            <div className="absolute right-0 top-[220px] max-w-[300px] text-right">
              <AnimatePresence mode="wait">
                <motion.p
                  key={`desc-${activeFeature}`}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="text-[14px] font-medium text-slate-500 leading-relaxed italic"
                >
                  "{feature.desc}"
                </motion.p>
              </AnimatePresence>
            </div>

            {/* ── Central Voice Orb ── */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="relative">
                {/* Glow halo */}
                <div className="absolute inset-0 bg-purple-500/20 blur-[80px] rounded-full scale-[1.8]" />

                {/* Orb */}
                <motion.div
                  className="relative w-56 h-56 rounded-full bg-slate-900 shadow-2xl overflow-hidden flex items-center justify-center border-4 border-white"
                  animate={{ scale: [1, 1.025, 1] }}
                  transition={{ repeat: Infinity, duration: 4, ease: 'easeInOut' }}
                >
                  <div className="absolute inset-0 bg-linear-to-t from-transparent via-purple-500/10 to-transparent" />

                  {/* Blurred color blob */}
                  <div className="absolute w-[80%] h-[55%] bg-linear-to-r from-pink-500 via-purple-500 to-orange-400 opacity-80 blur-2xl rounded-full animate-pulse" />

                  {/* Waveform bars */}
                  <div className="relative flex items-center justify-center gap-1.5 w-full px-6">
                    {[...Array(12)].map((_, i) => (
                      <motion.div
                        key={i}
                        animate={{
                          height: [12, 55, 22, 48, 16],
                          background: [
                            'linear-gradient(to top, #A855F7, #EC4899)',
                            'linear-gradient(to top, #EC4899, #F97316)',
                            'linear-gradient(to top, #A855F7, #EC4899)',
                          ],
                        }}
                        transition={{ repeat: Infinity, duration: 2, delay: i * 0.1, ease: 'easeInOut' }}
                        className="w-2 rounded-full opacity-90"
                      />
                    ))}
                  </div>

                  {/* Lukos real logo overlay */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-white/20 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/30 shadow-sm">
                    <img
                      src="/icons/Logo4kIcon.png"
                      alt="Lukos"
                      className="h-4 w-auto object-contain brightness-0 invert"
                    />
                  </div>
                </motion.div>

                {/* ── Feature nav arrows ── */}
                <button
                  onClick={goPrev}
                  className="absolute -left-14 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:text-purple-600 hover:border-purple-300 transition-all hover:shadow-lg"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={goNext}
                  className="absolute -right-14 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md flex items-center justify-center text-slate-600 hover:text-purple-600 hover:border-purple-300 transition-all hover:shadow-lg"
                >
                  <ChevronRight size={16} />
                </button>

                {/* Feature label below orb */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`label-${activeFeature}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute -bottom-14 left-1/2 -translate-x-1/2 text-center whitespace-nowrap"
                  >
                    <div className="flex items-center gap-2 bg-white border border-slate-100 shadow-sm px-4 py-2 rounded-full">
                      <feature.icon size={13} style={{ color: feature.accentColor }} />
                      <span className="text-[12px] font-black text-slate-700">{feature.title}</span>
                    </div>
                    {/* Dots */}
                    <div className="flex items-center justify-center gap-1.5 mt-3">
                      {features.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveFeature(i)}
                          className="rounded-full transition-all duration-300"
                          style={{
                            width: i === activeFeature ? 18 : 6,
                            height: 6,
                            backgroundColor: i === activeFeature ? feature.accentColor : '#CBD5E1',
                          }}
                        />
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Partners */}
        <div className="w-full mt-24 py-10 px-6 border-t border-slate-100">
          <div className="max-w-4xl mx-auto flex flex-col items-center gap-5">
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-center">
              Inteligência operando milhões de transações diárias com segurança blindada.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16 opacity-35">
              {partners.map(p => (
                <div key={p.name} className="flex items-center gap-2 hover:opacity-100 transition-opacity">
                  <span className="text-xl grayscale">{p.icon}</span>
                  <span className="text-sm font-black text-slate-600 uppercase tracking-widest">{p.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LukIAVoiceHero;
